import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox/Checkbox";
import { getIsIncludeProductsChecked, getIsVariantWithIncludes } from "@thkStore/Store/Selectors/IncludeProductsSelectors";
import setIncludeProducts from "@thkStore/Store/Widgets/IncludeProducts/Handlers/SetIncludeProducts";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {
    fields: {
        label: string;
    };
    extendedStyles?: ProductDetailsIncludeProductsCheckboxStyles;
}

const mapStateToProps = (state: ApplicationState, props: HasProductContext) => {
    const { product, productInfo } = props.productContext;
    const { selectedProductId, configurationSelection, configurationCompleted, variantSelectionCompleted } =
        state.pages.productDetails;

    return {
        isAuthenticated: state.context.session.isAuthenticated,
        productId: selectedProductId,
        isVariantWithIncludes: getIsVariantWithIncludes(product),
        // Mirror ProductDetailsAddToCartButton visibility so the checkbox only shows when Add to Cart shows
        canAddToCart: canAddToCart(
            state,
            product,
            productInfo,
            configurationCompleted,
            variantSelectionCompleted,
            configurationSelection,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
        failedToLoadInventory: productInfo.failedToLoadInventory,
        checked: getIsIncludeProductsChecked(state, selectedProductId),
    };
};

const mapDispatchToProps = {
    setIncludeProducts,
};

type Props = OwnProps &
    HasProductContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface ProductDetailsIncludeProductsCheckboxStyles {
    checkbox?: CheckboxPresentationProps;
}

export const includeProductsCheckboxStyles: ProductDetailsIncludeProductsCheckboxStyles = {
    checkbox: {
        css: css`
            margin: 16px 0;
        `,
    },
};

const ProductDetailsIncludeProductsCheckbox: React.FC<Props> = ({
    isAuthenticated,
    productId,
    isVariantWithIncludes,
    canAddToCart,
    hasEnoughInventory,
    failedToLoadInventory,
    checked,
    fields,
    extendedStyles,
    setIncludeProducts,
}) => {
    const [styles] = React.useState(() => mergeToNew(includeProductsCheckboxStyles, extendedStyles));

    // Domain gate: only relevant for authenticated users on a variant product that has includes
    if (!isAuthenticated || !isVariantWithIncludes || !productId) {
        return null;
    }

    // Same condition as ProductDetailsAddToCartButton: hide when out of stock or can't add to cart
    if ((!hasEnoughInventory && !failedToLoadInventory) || !canAddToCart) {
        return null;
    }

    const handleChanged = (_: React.SyntheticEvent, value: boolean) => {
        setIncludeProducts({ productId, checked: value });
    };

    return (
        <Checkbox
            {...styles.checkbox}
            checked={checked}
            onChange={handleChanged}
            data-test-selector="productDetailsIncludeProductsCheckbox"
        >
            {fields.label || translate("Include Products")}
        </Checkbox>
    );
};

const widgetModule: WidgetModule = {
    component: withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductDetailsIncludeProductsCheckbox)),
    definition: {
        displayName: "Include Products Checkbox",
        group: "Product Details",
        icon: "clipboard",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: "label",
                displayName: "Checkbox Label",
                editorTemplate: "TextField",
                defaultValue: "Include Products",
                fieldType: "Translatable",
                isRequired: false,
                tooltip: "Label shown next to the checkbox. If empty, the default value is used.",
            },
        ],
    },
};

export default widgetModule;
